/**
 * English message catalogue.
 *
 * Marketing copy follows `GrowthWise Education Revised 1.docx` (extracted
 * text kept at docs/source-content-revised-1.txt), which repositions the
 * site from resilience after setbacks to education for life, with failure
 * education as the signature focus. Copy is adapted for the web rather than
 * transcribed, but nothing here invents a course, price, credential,
 * testimonial, partner organisation or contact detail; where the source
 * document is silent, the UI says so or the item is recorded in
 * docs/open-questions.md.
 *
 * This object's shape is the contract every other language must satisfy --
 * `zh-CN.ts` is typed against it, so a missing translation is a build error
 * rather than an English string leaking into a Chinese page.
 */
export const en = {
  meta: {
    siteName: "GrowthWise Education",
    tagline: "Education for life",
    home: {
      title: "GrowthWise Education | Life happens. Learn how to respond.",
      description:
        "Education for life. GrowthWise helps people understand how setbacks, pressure, and difficult emotions affect confidence and performance, and build the resilience to keep growing.",
    },
    about: {
      title: "About GrowthWise Education | Education for life",
      description:
        "Our mission and approach: education should prepare people not only to succeed, but to respond constructively when life does not go as planned.",
    },
    services: {
      title: "Programs and Services | GrowthWise Education",
      description:
        "Student programs, parent education, professional and executive programs, group training, and individual consultation. On-site and remote delivery.",
    },
    assessment: {
      title: "GrowthWise Check-In | GrowthWise Education",
      description:
        "A private self-reflection of about 90 seconds on how a difficult experience may be affecting you. Responses stay in your browser and are never saved.",
    },
    partnerships: {
      title: "Partnerships | GrowthWise Education",
      description:
        "Long-term partnerships with universities, K–12 schools, and organizations to build resilience, emotional wellbeing, and life-readiness.",
    },
    careers: {
      title: "Careers: Research and Assessment | GrowthWise Education",
      description:
        "Join GrowthWise in researching how difficult experiences, emotion, and resilience shape self-perception and performance.",
    },
    contact: {
      title: "Contact | GrowthWise Education",
      description:
        "Tell us what you or your organization is facing. We will help identify the most useful starting point.",
    },
    videoIntro: {
      title: "Video Introduction | GrowthWise Education",
      description:
        "Record a short video introduction from your browser as part of your GrowthWise Education application.",
    },
    privacy: {
      title: "Privacy Notice | GrowthWise Education",
      description: "What information GrowthWise Education collects and why.",
    },
    terms: {
      title: "Terms of Use | GrowthWise Education",
      description:
        "Terms that apply to the GrowthWise Education website and portal.",
    },
    notFound: {
      title: "Page not found | GrowthWise Education",
      description: "The page you requested does not exist.",
    },
  },

  nav: {
    skipToContent: "Skip to main content",
    primary: "Primary navigation",
    home: "Home",
    about: "About",
    services: "Programs",
    assessment: "Check-In",
    partnerships: "Partnerships",
    careers: "Careers",
    contact: "Contact",
    signIn: "Sign in",
    signOut: "Sign out",
    account: "My account",
    admin: "Admin",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    language: "Language",
    languageEnglish: "English",
    languageChinese: "简体中文",
  },

  footer: {
    explore: "Explore",
    account: "Account",
    legal: "Legal",
    privacy: "Privacy notice",
    terms: "Terms of use",
    rights: "All rights reserved.",
    crisisHeading: "Urgent support",
    // Reproduced exactly from the original source document; no other crisis
    // resource is listed because the documents name only this one.
    crisisBody:
      "For urgent or crisis support in the United States, call or text 988. In other countries, contact the appropriate local emergency or crisis-support service.",
    notMedical:
      "GrowthWise Education provides educational programs. It is not a substitute for assessment or care from a qualified healthcare or mental-health professional.",
  },

  home: {
    heroTitleLine1: "Life happens.",
    heroTitleLine2: "Learn how to respond.",
    heroBody:
      "School prepares us for exams and careers, but life asks more of us. Setbacks, change, and uncertainty are part of every life, and how we meet them shapes who we become. GrowthWise helps people understand these moments and grow through them with resilience, confidence, and clarity.",
    pillarsHeading: "What we help people do",
    pillars: {
      understand: {
        title: "Understand the experience",
        body: "Make sense of what happened and why it affects you the way it does.",
      },
      protect: {
        title: "Protect your confidence",
        body: "Keep a setback from becoming a verdict on who you are.",
      },
      build: {
        title: "Build resilience",
        body: "Strengthen your capacity to recover, adapt, and decide well.",
      },
      grow: {
        title: "Keep growing",
        body: "Return to the goals that matter, with greater perspective.",
      },
    },
    missionEyebrow: "Our Mission",
    missionTitle:
      "Preparing people for success, and for when life does not go as planned",
    missionBody:
      "Schools, families, and workplaces invest heavily in helping people succeed. Far less attention goes to the moments when things fall apart: failure, rejection, pressure, loss, and sudden change. Yet those moments shape confidence, judgment, and performance for years. Our mission is to help people understand these experiences and grow through them, so that a setback becomes part of their story rather than the end of it.",
    missionLink: "More about us",
    missionImageAlt:
      "Illustration: a hiker reaches back to help another climb a rocky slope.",
    focusEyebrow: "Our focus",
    focusTitle: "Failure Education",
    focusBody:
      "Everyone is taught to pursue success; far fewer are taught what to do when they fail. Research shows that learning from failure does not happen automatically: it takes guided meaning-making for a setback to become insight and fuel for future success. That guidance is what Failure Education provides. We help people understand what failure does emotionally, psychologically, and behaviorally, catch a setback before it hardens into identity, and turn the experience into growth. Our four-stage framework puts that process into practice.",
    frameworkHeading: "Our learning framework",
    modelLink: "Explore the framework",
    servicesEyebrow: "Programs",
    servicesSubtitle:
      "For students, families, professionals, and organizations",
    servicesLink: "See all programs",
    servicesStartTitle: "Not sure where to start?",
    servicesStartBody:
      "Tell us what you or your organization is facing, and we will suggest the most useful starting point.",
    servicesStartLink: "Talk with us",
    assessmentEyebrow: "Self-reflection tool",
    assessmentLink: "Begin the check-in",
    partnersEyebrow: "Work with us",
    partnersSubtitle:
      "We partner with schools, universities, and organizations, and we are growing our research team.",
    partners: {
      universities: {
        title: "Universities",
        body: "Joint research, co-developed courses and certificates, and program evaluation.",
      },
      schools: {
        title: "K–12 schools",
        body: "Student workshops, resilience curricula, teacher training, and the Resilient School Initiative.",
      },
      organizations: {
        title: "Organizations",
        body: "Sponsorship, employee and leadership programs, and leadership resilience.",
      },
      researchers: {
        title: "Researchers",
        body: "Help us study how difficult experiences, emotion, and resilience shape performance.",
      },
    },
    partnersLink: "Explore partnerships",
    careersLink: "View research roles",
    ctaTitle: "What Comes Next Can Be Different",
    ctaBody:
      "Tell us what you or your organization is facing. We will help identify the most useful starting point.",
    ctaButton: "Contact us",
    ctaSecondary: "Create an account",
  },

  approach: {
    title: "What Makes GrowthWise Different",
    body: "GrowthWise occupies a distinctive space between traditional education, motivational training, and clinical mental-health services. We do not simply teach people how to succeed, encourage them to “try harder,” or treat psychological conditions. We focus on what happens between experience and performance.",
    chainIntro: "Our work is built around the relationship:",
    chainLabel: "How a difficult experience shapes performance",
    chain: [
      "Experience",
      "Emotion",
      "Self-perception",
      "Motivation and judgment",
      "Behavior",
      "Performance",
    ],
    note: "Failure and setbacks are a signature focus because they make this process especially visible, but our mission is broader. GrowthWise helps people understand how difficult experiences shape the way they think, feel, decide, and perform—and develop the resilience, confidence, judgment, and adaptability needed to respond constructively throughout life.",
  },

  about: {
    eyebrow: "About us",
    title: "About GrowthWise Education",
    subtitle:
      "We see this as education for life—learning not only how to succeed, but how to respond when life does not go as planned.",
    whoHeading: "Who We Are",
    whoBody:
      "GrowthWise Education is a multidisciplinary education and human-development company dedicated to helping people better understand themselves and develop the inner capacities needed to navigate learning, work, relationships, and life. Drawing on education, psychology, behavioral science, law, and real-world experience, GrowthWise brings together leading educators, psychologists, researchers, and legal scholars to address an essential but often overlooked dimension of education: how people respond when life becomes difficult.",
    missionHeading: "Our Mission",
    missionBody:
      "Our mission is to help people understand how setbacks, pressure, uncertainty, disappointment, and change affect the way they think, feel, decide, and perform—and to develop the resilience, confidence, judgment, and self-awareness needed to move forward constructively.",
    missionDetail:
      "We believe education should prepare people not only to achieve and succeed, but also to recover, adapt, preserve a healthy sense of self-worth, make sound choices under pressure, and continue growing when life does not go as planned.",
    coreIdeaLabel: "Core idea",
    coreIdeaBody:
      "We do not erase difficult experiences. We help people learn from them without being defined by them.",
    coreIdeaDiagram:
      "A line dips at a difficult experience, then rises as the person learns and grows from it, while a fading dashed branch shows the path of being defined by it.",
    coreIdeaSetback: "Difficult experience",
    coreIdeaGrow: "Learn and grow from it",
    coreIdeaDefined: "Defined by it",
    prepareHeading:
      "Preparing People for Success—and for What Happens When Life Does Not Go as Planned",
    prepareBody: [
      "GrowthWise Education broadens the purpose of education beyond knowledge, skills, and achievement. Schools, universities, families, and workplaces invest heavily in preparing people to succeed, yet far less attention is given to helping them understand and manage the emotional impact of failure, disappointment, rejection, pressure, uncertainty, loss, conflict, and major change.",
      "These experiences matter because performance is shaped not only by ability, but also by emotion, self-perception, motivation, judgment, and behavior. Difficult experiences can weaken confidence, distort self-worth, reduce motivation, affect decision-making, and influence how people approach future opportunities.",
      "GrowthWise addresses this often-overlooked dimension of education by helping people understand the connection between experience, emotion, self-perception, behavior, and performance, while developing the resilience, self-awareness, confidence, and adaptability needed to respond constructively and continue growing.",
    ],
    usableBody:
      "A person may possess substantial knowledge and ability yet still struggle to perform when confidence collapses, anxiety becomes overwhelming, rejection changes self-perception, or failure creates fear of trying again.",
    usableClosing:
      "GrowthWise develops the inner capacities that allow knowledge and ability to remain usable under real-life conditions.",
    lifeHeading: "Education for life includes the development of:",
    lifeItems: [
      "Self-awareness",
      "Emotional understanding",
      "Confidence",
      "Healthy self-worth",
      "Resilience",
      "Motivation",
      "Judgment",
      "Adaptability",
      "Agency",
      "Relationships",
      "Learning from experience",
    ],
    beingNote:
      "This broader approach to personal development—sometimes referred to philosophically as “Being Education”—does not replace traditional education. It complements it.",
    visionHeading: "Our Vision",
    visionBody:
      "We envision a future in which education prepares people not only to achieve, but also to face adversity, uncertainty, change, and disappointment with greater resilience and perspective.",
    visionDetail:
      "In that future, students do not define themselves by failure, parents and educators know how to respond constructively to setbacks, and schools and workplaces treat difficult experiences as opportunities for learning rather than sources of fear or stigma.",
    visionGoal:
      "Our long-term vision is to make emotional resilience, healthy self-worth, and the ability to learn from setbacks an integral part of education throughout life.",
  },

  app: {
    heading: "The GrowthWise Education App",
    body: "The app extends learning into everyday life through short lessons, private reflections, educational check-ins, and practical exercises. It complements, rather than replaces, human guidance, healthcare, or professional mental-health services.",
    point1:
      "Private reflection: personal notes and emotional check-ins remain private.",
    point2: "Small, practical steps: short exercises designed for real days.",
    point3:
      "Connected learning: continue between workshops, consultations, and group sessions.",
  },

  services: {
    eyebrow: "Programs and services",
    title: "Practical education for every stage of life",
    subtitle:
      "Our programs focus on how difficult experiences and emotions affect confidence, self-worth, motivation, judgment, and performance, and how people can respond with greater resilience and perspective.",
    items: {
      students: {
        index: "01",
        title: "Student Programs",
        summary:
          "Helping middle-school through graduate students handle academic setbacks, performance pressure, perfectionism, and major transitions.",
        body: "For middle-school, high-school, university, and graduate students. We help students understand and manage academic setbacks, performance pressure, perfectionism, fear of failure, rejection, loss of confidence, and major transitions.",
        detail:
          "The goal is to help students strengthen resilience, protect their self-worth, rebuild motivation, and find the confidence to re-engage after difficult experiences.",
        points: [
          "Workshops",
          "Multi-session courses",
          "Summer programs",
          "Leadership training",
          "Online learning and digital modules",
        ],
      },
      parents: {
        index: "02",
        title: "Parent Education",
        summary:
          "Helping parents see how expectations, comparison, and reactions to setbacks shape a child's confidence and self-worth.",
        body: "Parents strongly influence how children interpret success, disappointment, and failure. We help parents understand how expectations, comparison, praise, criticism, and their own reactions to setbacks can affect a child's confidence, motivation, and sense of self-worth.",
        detail:
          "Topics include academic pressure, supporting high-achieving students, fear of failure, talking with a child after a setback, and building resilience without minimizing difficult emotions.",
        points: [
          "Workshops",
          "Webinars",
          "Family courses",
          "Educational consultations",
        ],
      },
      professionals: {
        index: "03",
        title: "Professional and Executive Programs",
        summary:
          "Helping professionals and leaders respond to career setbacks, failed projects, criticism, change, and leadership pressure.",
        body: "We help professionals and leaders respond more effectively to career setbacks, failed projects, criticism, organizational change, uncertainty, and leadership pressure.",
        detail:
          "Programs focus on professional resilience, decision-making under emotional pressure, confidence after setbacks, adaptive leadership, professional identity, and team recovery.",
        points: [
          "Individuals",
          "Leadership teams",
          "Companies",
          "Professional associations",
          "Nonprofit institutions",
        ],
      },
      group: {
        index: "04",
        title: "Group Training",
        summary:
          "Facilitated programs that combine structured learning, guided reflection, case studies, and peer learning.",
        body: "Facilitated group programs that combine structured education, guided reflection, case studies, practical exercises, discussion, and peer learning.",
        detail:
          "Group training is especially valuable for people who benefit from seeing how others respond to similar pressures and setbacks.",
        points: [
          "Multi-week courses",
          "Cohort programs",
          "Student groups",
          "Parent groups",
          "Leadership groups",
        ],
      },
      individual: {
        index: "05",
        title: "Individual Consultation",
        summary:
          "One-to-one educational guidance on difficult experiences, emotional responses, confidence, and next steps.",
        body: "One-to-one educational consultation focused on difficult experiences, emotional responses, confidence, performance patterns, and strategies for moving forward.",
        detail:
          "These services are educational, not clinical. GrowthWise does not diagnose or treat mental-health conditions. Where clinical concerns arise, we encourage people to seek support from qualified healthcare or mental-health professionals.",
        points: [
          "One-to-one sessions",
          "Single or multi-session",
          "On-site or remote",
        ],
      },
    },
    deliveryTitle: "How we deliver",
    deliveryOnSiteLabel: "On site",
    deliveryOnSiteBody: "Workshops, lectures, and individual guidance",
    deliveryRemoteLabel: "Remote",
    deliveryRemoteBody: "Programs and consultation across locations",
    deliveryAppLabel: "Digital",
    deliveryAppBody: "Online learning, reflection, and check-in tools",
    // The source documents do not publish prices, durations or credentials,
    // so the site does not state any. This note is shown instead of inventing
    // them.
    pricingNoteHeading: "Fees and scheduling",
    pricingNoteBody:
      "Programs are scoped to each audience, so fees and schedules are confirmed directly. Tell us what you are facing and we will outline the options.",
    cta: "Discuss a program",
  },

  model: {
    title: "The GrowthWise Learning Framework",
    intro:
      "GrowthWise programs follow a four-stage learning process designed to help people understand difficult experiences, reduce their negative impact, and move forward with greater resilience and confidence.",
    steps: {
      recognize: {
        number: "1",
        title: "Recognize",
        summary:
          "Notice the emotions, thoughts, and reactions that follow a difficult experience, and separate them from who you are.",
        body: "Identify the emotions, thought patterns, changes in motivation, and behavioral responses that follow difficult experiences. Greater awareness helps separate the experience itself from a person's identity and self-worth.",
      },
      reframe: {
        number: "2",
        title: "Reframe",
        summary:
          "See what happened more accurately: a setback is not a verdict on your worth or your future.",
        body: "Develop a more balanced and accurate understanding of what happened. Participants learn to distinguish setbacks from personal worth, temporary difficulty from permanent inability, and emotional reactions from objective reality.",
      },
      rebuild: {
        number: "3",
        title: "Rebuild",
        summary:
          "Restore confidence, motivation, emotional balance, and a sense of agency.",
        body: "Strengthen the internal resources needed to recover and move forward, including confidence, self-worth, motivation, emotional regulation, sound judgment, and a renewed sense of agency.",
      },
      reengage: {
        number: "4",
        title: "Re-engage",
        summary:
          "Turn understanding into action and pursue meaningful goals again.",
        body: "Translate understanding into action by approaching challenges again, making decisions with greater perspective, using feedback constructively, rebuilding productive routines, and pursuing meaningful goals without allowing past setbacks to define future possibilities.",
      },
    },
  },

  assessment: {
    eyebrow: "Self-reflection tool",
    title: "GrowthWise Check-In",
    subtitle: "A private reflection of about 90 seconds",
    intro:
      "A brief educational self-reflection to help you consider how a difficult experience may be affecting your emotions, confidence, motivation, self-worth, behavior, relationships, and performance. Its purpose is to build awareness and point you toward relevant educational resources. Responses are completed in your browser and are not transmitted or saved.",
    disclaimerHeading: "Important distinction",
    disclaimerBody:
      "The GrowthWise Check-In is not a validated clinical instrument and is not intended to diagnose mental-health conditions. It is not a substitute for assessment or care from a qualified healthcare or mental-health professional.",
    privacyNotice:
      "Nothing you select on this page leaves your device. No answer is sent to our servers, and closing the page discards it.",
    questionsHeading: "Reflection Questions",
    questions: [
      "During the past two weeks, how often have thoughts about a difficult experience been hard to set aside?",
      "How much has the experience affected your confidence in making decisions?",
      "How often have you avoided people, places, or tasks connected with the experience?",
      "How much has the experience affected your sleep, concentration, or daily energy?",
      "How difficult has it felt to imagine a positive next step?",
      "How often have you felt defined by what happened rather than by the whole of who you are?",
    ],
    optionsLabel: "Response options",
    options: [
      "Not at all",
      "Several days",
      "More than half the days",
      "Nearly every day",
    ],
    progressLabel: "{answered} of {total} answered",
    submit: "See my reflection",
    reset: "Start again",
    incomplete: "Answer every question to see your reflection.",
    resultEyebrow: "Your reflection",
    resultTitle: "What your responses suggest",
    resultSubtitle: "Proportionate guidance, not a clinical diagnosis",
    nextStepLabel: "Possible next step",
    bands: {
      lower: {
        label: "Lower current impact",
        headline: "You may have useful room to reflect and strengthen.",
        body: "The experience appears to be having a relatively limited effect on daily life. Educational programs and preventive resilience practices may help build on that stability.",
        nextStep:
          "Consider a workshop, a short course, or a self-guided digital module.",
      },
      moderate: {
        label: "Moderate current impact",
        headline: "A structured next step may be helpful.",
        body: "The experience may still be affecting confidence, attention, or engagement. Focused guidance may help identify the pattern and a manageable next action.",
        nextStep:
          "Consider an individual consultation or a facilitated group program.",
      },
      higher: {
        label: "Higher current impact",
        headline: "You deserve support with this, not simply more pressure.",
        body: "The responses suggest a meaningful level of current distress. Educational programming may be useful alongside appropriate personal or professional support.",
        nextStep:
          "Consider contacting a qualified mental-health professional or healthcare provider.",
      },
    },
  },

  partnerships: {
    eyebrow: "Partnerships",
    title: "Building a culture of resilience, together",
    subtitle:
      "GrowthWise develops long-term partnerships with educational institutions and organizations that want to strengthen resilience, emotional wellbeing, and life-readiness across their communities.",
    intro:
      "We are more than an outside workshop provider. We work with partners to design programs, develop curricula, evaluate outcomes, and build lasting educational capacity.",
    universities: {
      title: "Universities and Higher Education",
      body: "We collaborate with schools of education, psychology departments, student affairs offices, wellness programs, international student services, leadership centers, executive education programs, and research institutes.",
      detail:
        "Partnerships can focus on failure education, resilience, confidence, emotional wellbeing, student development, and performance.",
      points: [
        "Joint research",
        "Co-developed courses and certificates",
        "Pilot initiatives",
        "Faculty collaboration",
        "Program evaluation",
        "Conferences and forums",
      ],
    },
    schools: {
      title: "K–12 Schools",
      body: "We work with public, independent, charter, and international schools to integrate resilience and failure education into student development.",
      points: [
        "Student workshops",
        "Resilience curricula",
        "Parent education",
        "Teacher training",
        "High-achiever support",
        "Transition programs",
        "School-wide wellbeing initiatives",
      ],
    },
    resilientSchool: {
      badge: "Flagship initiative",
      title: "GrowthWise Resilient School Initiative",
      body: "For schools seeking a more comprehensive approach, the initiative brings together student programming, teacher development, parent education, digital resources, assessment, follow-up support, and institutional reporting.",
      detail:
        "The goal is an ongoing partnership that builds a stronger culture of resilience, one in which students are better prepared to understand setbacks, manage difficult emotions, protect their confidence and self-worth, and keep learning and growing.",
      points: [
        "Student programming",
        "Teacher development",
        "Parent education",
        "Digital resources",
        "Assessment",
        "Follow-up support",
        "Institutional reporting",
      ],
    },
    corporate: {
      title: "Corporate Sponsorship and Collaboration",
      body: "We partner with corporations, professional-service firms, foundations, entrepreneurship programs, accelerators, leadership institutes, and professional associations that share our interest in resilience, emotional wellbeing, education, and human development.",
      formsHeading: "Ways to collaborate",
      forms: [
        "Sponsoring student and community programs",
        "Supporting research and assessment",
        "Co-developed educational initiatives",
        "Employee and leadership programs",
        "Public forums",
        "Scholarships",
        "Digital learning resources",
        "Joint campaigns",
      ],
      topicsHeading: "Topics for organizations",
      topics: [
        "Leadership resilience",
        "Decision-making under pressure",
        "Team recovery after unsuccessful projects",
        "Organizational change",
        "Professional confidence",
        "Psychological safety",
        "Learning from intelligent failure",
      ],
      closing:
        "We aim for partnerships that create both social impact and organizational value, helping individuals and communities prepare for pressure, setbacks, uncertainty, and change.",
    },
    ctaTitle: "Explore a partnership",
    ctaBody:
      "Tell us about your institution or organization and what you hope to build. We will follow up to explore the right model.",
    ctaButton: "Start a conversation",
  },

  careers: {
    eyebrow: "Careers",
    title: "Research and Assessment",
    subtitle:
      "GrowthWise is seeking researchers and assessment professionals interested in the relationship between difficult experiences, emotion, resilience, self-perception, and performance.",
    agendaHeading: "Our research agenda",
    agenda: [
      "How students interpret failure",
      "Setbacks and confidence",
      "Emotional responses to academic difficulty",
      "Performance pressure",
      "Perfectionism",
      "Resilience",
      "Parent responses to setbacks",
      "International student adjustment",
      "Cultural attitudes toward achievement and failure",
    ],
    contributeHeading: "What you may contribute to",
    contribute: [
      "Literature reviews and research design",
      "Developing educational surveys and assessment tools",
      "Collecting and analyzing quantitative and qualitative data",
      "Evaluating GrowthWise programs and learning outcomes",
      "Preparing research reports, articles, and educational materials",
      "Developing evidence-based interventions",
      "Collaborating with universities, schools, and research partners",
    ],
    backgroundHeading: "Who we are looking for",
    backgroundBody:
      "Backgrounds in psychology, education, human development, behavioral science, counseling, assessment, statistics, or related fields. Experience in research methodology, program evaluation, psychometrics, or cross-cultural research is especially valuable.",
    goalHeading: "Why this work matters",
    goalBody:
      "GrowthWise aims to build a strong research foundation for its programs and, over time, to develop validated assessment tools and evidence-based approaches that help individuals and institutions better understand and respond to failure, setbacks, difficult emotions, and resilience.",
    arrangementsHeading: "Ways to work with us",
    arrangementsBody:
      "We welcome inquiries from researchers, graduate students, postdoctoral scholars, faculty members, and experienced professionals.",
    arrangements: [
      "Full-time",
      "Part-time",
      "Project-based",
      "Research collaboration",
    ],
    ctaTitle: "Interested in joining us?",
    ctaBody:
      "Send us a short note about your background and interests. You can also create an account to submit an application with a video introduction.",
    ctaButton: "Send an inquiry",
    ctaSecondary: "Create an account to apply",
  },

  contact: {
    eyebrow: "Contact",
    title: "What Comes Next Can Be Different",
    subtitle:
      "Tell us what you or your organization is facing. We will help identify the most useful starting point.",
    siteLabel: "Visit GrowthWise Education Online",
    formHeading: "Send a message",
    formIntro: "You do not need an account to send this message.",
    successHeading: "Message received",
    successBody:
      "Thank you. We have your message and will reply to the address you provided.",
    sendAnother: "Send another message",
    checkInPrompt: "Want to try the self-reflection tool?",
  },
  form: {
    name: "Full name",
    email: "Email address",
    organization: "Organization",
    organizationOptional: "Organization (optional)",
    phone: "Contact phone (optional)",
    message: "What are you facing?",
    messageHint:
      "A few sentences is enough. Please do not include sensitive health details.",
    interest: "What are you interested in?",
    audience: "Who is this for?",
    delivery: "Preferred delivery",
    preferredLanguage: "Preferred language for replies",
    required: "required",
    optional: "optional",
    selectPlaceholder: "Select an option",
    charactersRemaining: "{count} characters remaining",
    consentContact:
      "I agree that GrowthWise Education may contact me at the address above about my enquiry.",
    consentPrivacy:
      "I have read the privacy notice and agree to how my information is handled.",
    submit: "Send message",
    submitting: "Sending…",
    saveDraft: "Save draft",
    saving: "Saving…",
    saved: "Draft saved",
    submitApplication: "Submit application",
    interests: {
      lectures_and_seminars: "Lectures and seminars",
      individual_consulting: "Individual consulting",
      group_training: "Group training",
      digital_learning_and_support: "Digital learning and support",
      not_sure: "Not sure yet",
    },
    audiences: {
      individual: "An individual",
      educational_community: "An educational community",
      organization: "An organization",
      community: "A community group",
    },
    deliveries: {
      on_site: "On site",
      remote: "Remote",
      either: "Either",
    },
  },

  auth: {
    signInTitle: "Sign in",
    signInSubtitle: "Access your applications and recordings.",
    signUpTitle: "Create an account",
    signUpSubtitle:
      "An account lets you save a draft application, submit it, and record a video introduction.",
    password: "Password",
    passwordConfirm: "Confirm password",
    passwordHint: "At least 12 characters.",
    displayName: "Your name",
    signInButton: "Sign in",
    signUpButton: "Create account",
    signOutButton: "Sign out",
    forgotPassword: "Forgot your password?",
    noAccount: "No account yet?",
    hasAccount: "Already have an account?",
    createOne: "Create one",
    signInInstead: "Sign in",
    resetTitle: "Reset your password",
    resetSubtitle:
      "Enter your email address and we will send a link to choose a new password.",
    resetButton: "Send reset link",
    resetSentHeading: "Check your email",
    resetSentBody:
      "If an account exists for {email}, a password reset link is on its way. The link expires after a short time.",
    verifyTitle: "Verify your email address",
    verifyBody:
      "We sent a verification link to {email}. Open it to confirm the address, then return here.",
    verifyResend: "Resend verification email",
    verifyResent: "Verification email sent.",
    verifyRefresh: "I have verified — continue",
    verifyStillPending:
      "This address is still unverified. Open the link in the email, then try again.",
    verifiedBanner: "Email address verified. Thank you.",
    verificationRequired:
      "Submitting an application and recording a video both require a verified email address.",
    sessionExpiredTitle: "Your session has expired",
    sessionExpiredBody:
      "For your security you have been signed out. Please sign in again.",
    passwordRequirements: "Use at least 12 characters.",
    passwordMismatch: "The two passwords do not match.",
    termsAgreement:
      "By creating an account you agree to the terms of use and the privacy notice.",
    // Google sign-in is deliberately absent: email and password is the
    // supported method, per the project brief.
  },

  account: {
    navOverview: "Overview",
    navApplication: "My application",
    navVideos: "My recordings",
    navProfile: "Profile",
    overviewTitle: "Welcome back, {name}",
    overviewSubtitle: "Your applications and recordings in one place.",
    statusHeading: "Account status",
    emailVerified: "Email verified",
    emailUnverified: "Email not verified",
    applicationsHeading: "Applications",
    videosHeading: "Recordings",
    newApplication: "Start an application",
    continueDraft: "Continue draft",
    viewApplication: "View",
    noApplications: "You have not started an application yet.",
    noVideos: "You have not recorded a video introduction yet.",
    recordVideo: "Record a video introduction",
    profileTitle: "Your profile",
    profileSubtitle: "Update the details we use to contact you.",
    profileEmailNote:
      "Your sign-in email cannot be changed here. Contact us if you need it updated.",
    profileSaved: "Profile updated.",
    applicationTitle: "Application",
    applicationSubtitle:
      "Save a draft at any time. You can submit once every required field is complete.",
    applicationSubmittedHeading: "Application submitted",
    applicationSubmittedBody:
      "We have your application. You can still view it here, and we will contact you about next steps.",
    draftBadge: "Draft",
    deleteDraft: "Delete draft",
    deleteDraftConfirmTitle: "Delete this draft?",
    deleteDraftConfirmBody:
      "The draft and any recordings attached to it will be deleted. This cannot be undone.",
    deleteVideoConfirmTitle: "Delete this recording?",
    deleteVideoConfirmBody:
      "The video file will be deleted permanently. This cannot be undone.",
    confirmDelete: "Yes, delete",
    cancel: "Cancel",
    deleted: "Deleted.",
    deletePartial:
      "The record was removed, but one file could not be deleted yet. It is queued for retry.",
    statusLabel: "Status",
    createdLabel: "Created",
    updatedLabel: "Last updated",
    submittedLabel: "Submitted",
    attachedVideos: "{count} recording(s) attached",
    videosTitle: "Your recordings",
    videosSubtitle:
      "Recordings are private. Only you and GrowthWise Education staff can play them back.",
    videoFormat: "Format",
    videoSize: "File size",
    videoSource: "Recorded with",
    videoSources: {
      browser_recording: "This website",
      file_upload: "Uploaded file",
    },
    playVideo: "Play",
    hideVideo: "Hide",
    deleteVideo: "Delete recording",
    videoDeleted: "Recording deleted.",
    videoDeleteQueued:
      "The recording was removed from your account. The stored file is queued for deletion.",
    playbackExpired:
      "This playback link has expired. Reopen the recording to get a new one.",
  },

  contactStatus: {
    new: "New",
    in_progress: "In progress",
    answered: "Answered",
    spam: "Spam",
    archived: "Archived",
  },

  submissionStatus: {
    draft: "Draft",
    submitted: "Submitted",
    in_review: "In review",
    accepted: "Accepted",
    declined: "Declined",
    withdrawn: "Withdrawn",
    archived: "Archived",
  },

  videoStatus: {
    pending_upload: "Upload not finished",
    uploaded: "Ready",
    upload_failed: "Upload failed",
    delete_pending: "Deletion pending",
    deleted: "Deleted",
  },

  record: {
    title: "Video introduction",
    intro:
      "Record a short introduction from your browser. You can watch it back and record again before anything is uploaded.",
    permissionHeading: "Before you start",
    permissionBody:
      "A short spoken introduction tells us more about your situation than a form alone. When you select Enable camera, your browser asks permission to use your camera and microphone. Nothing is recorded or sent until you choose to start.",
    limitsDuration: "Maximum length: {duration}",
    limitsSize: "Maximum file size: {size}",
    limitsPrivacy:
      "Recordings are private. There is no public link, and playback requires an authorised, short-lived URL.",
    enableCamera: "Enable camera",
    requestingPermission: "Waiting for camera permission…",
    cameraOff: "The camera is off.",
    liveLabel: "Live camera preview",
    previewLabel: "Recording playback",
    start: "Start recording",
    stop: "Stop recording",
    processing: "Finishing the recording…",
    switchCamera: "Switch camera",
    turnOffCamera: "Turn off camera",
    recordingStatus: "Recording {elapsed} of {limit}",
    retake: "Record again",
    clipSummary: "Recording ready: {duration}, {size}, {type}",
    durationUnknown: "length unknown",
    limitDuration:
      "The maximum length was reached, so recording stopped automatically.",
    limitSize:
      "The maximum file size was reached, so recording stopped automatically.",
    upload: "Upload this recording",
    retryUpload: "Try the upload again",
    authorizing: "Authorising the upload…",
    uploadingLabel: "Uploading {sent} of {total}",
    cancelUpload: "Cancel upload",
    finalizing: "Checking the uploaded file…",
    uploadComplete: "Upload complete",
    uploadCompleteBody: "Your recording is saved and attached to your account.",
    uploadFailedHeading: "The upload did not finish",
    uploadFailedBody:
      "Your recording is still here, so you can try again without re-recording.",
    errorHeading: "Recording problem",
    errors: {
      insecureContext: "Camera access requires a secure connection",
      insecureContextBody:
        "Browsers only allow camera access over HTTPS. Open this page over HTTPS and try again.",
      unsupported:
        "This browser cannot record video in the page. Use the file upload option below instead.",
      permissionDenied:
        "Camera or microphone permission was refused. Allow access in your browser settings, then select Enable camera again.",
      noDevice:
        "No camera was found. Connect a camera, or use the file upload option below.",
      deviceBusy:
        "The camera is already in use by another application. Close that application and try again.",
      recordingFailed:
        "Recording stopped unexpectedly. Anything captured up to that point is kept below, so you can still review or upload it.",
      interrupted:
        "Recording was interrupted — the page lost focus, or a call came in. The footage captured so far has been kept.",
      emptyRecording: "No video data was captured. Please try recording again.",
      generic: "The camera could not be started. Please try again.",
    },
    fallbackHeading: "Upload a file instead",
    fallbackBody:
      "If you would rather record with your phone or computer camera app, you can upload the file here.",
    fallbackRequired:
      "This browser cannot record video in the page, so please record with another app and upload the file here.",
    chooseFile: "Choose a video file",
    preferFile: "I would rather upload a file",
    fileWrongType: "That file is not a supported video format.",
    fileTooLarge: "That file is larger than the {limit} limit.",
  },

  admin: {
    title: "Administration",
    subtitle: "Applications, messages, accounts and the audit trail.",
    confidentialityNote:
      "This console shows other people’s personal information and recordings. Access is logged.",
    navSubmissions: "Applications",
    navMessages: "Messages",
    navUsers: "Users",
    navAudit: "Audit log",

    submissionsTitle: "Applications",
    submissionsSubtitle: "Filter, review and update submitted applications.",
    messagesTitle: "Contact messages",
    messagesSubtitle: "Messages sent from the public contact form.",
    usersTitle: "Users",
    usersSubtitle: "Accounts, roles and access.",
    auditTitle: "Audit log",
    auditSubtitle:
      "A record of administrative actions. Entries cannot be edited or removed from here.",

    filterStatus: "Status",
    filterEmail: "Email",
    filterEmailPlaceholder: "name@example.org",
    filterFrom: "From date",
    filterTo: "To date",
    filterRole: "Role",
    filterAction: "Action",
    filterTarget: "Target",
    filterAny: "Any",
    applyFilters: "Apply",
    noResults: "No records match these filters.",
    open: "Open",
    exportCsv: "Export CSV",
    exportStarted: "Export downloaded.",
    previous: "Previous",
    next: "Next",
    page: "Page {page}",
    system: "System",

    colApplicant: "Applicant",
    colInterest: "Interest",
    colStatus: "Status",
    colSubmitted: "Submitted",
    colVideos: "Recordings",
    colActions: "Actions",
    colUser: "User",
    colRole: "Role",
    colVerified: "Verified",
    colLastSeen: "Last seen",
    colWhen: "When",
    colActor: "Administrator",
    colAction: "Action",
    colTarget: "Target",
    colDetails: "Details",

    applicationDetails: "Application details",
    videosHeading: "Recordings",
    noVideos: "No recordings are attached to this application.",
    updateStatus: "Update status",
    statusNote: "Note for this change (optional)",
    internalNotes: "Internal notes",
    internalOnly:
      "Visible to administrators only. Applicants never see these notes.",
    noNotes: "No internal notes yet.",
    addNote: "Add note",
    noteAdded: "Note added.",
    statusUpdated: "Status updated.",
    deleteSubmission: "Delete application",
    deleteConfirmTitle: "Delete this application?",
    deleteConfirmBody:
      "The application, its internal notes and its recordings will be deleted. This cannot be undone.",
    deleted: "Application deleted.",
    deletedWithPending:
      "Application deleted. {count} recording file(s) could not be removed yet and are queued for retry.",

    roles: {
      user: "User",
      admin: "Administrator",
    },
    grantAdmin: "Make administrator",
    revokeAdmin: "Remove administrator",
    disableUser: "Disable account",
    enableUser: "Enable account",
    disabled: "Disabled",
    userUpdated: "Account updated.",
    roleConfirmTitle: "Change this account’s role?",
    roleConfirmBody:
      "Administrators can read every application, every recording and every internal note. Changing the role for {email} signs that account out of its current sessions.",
    disableConfirmTitle: "Change account access?",
    disableConfirmBody:
      "A disabled account cannot sign in. This does not delete any data belonging to {email}.",

    antiAbuse: "Abuse check",
    antiAbuseNotChecked: "Not checked — no provider was configured",
    antiAbusePassed: "Passed",
    antiAbuseFailed: "Failed",

    bootstrapTitle: "Create the first administrator",
    bootstrapIntro:
      "There is no default administrator account. Use this page once, to grant the role to an account that already exists.",
    bootstrapRequirementsHeading:
      "This works only when all of the following are true",
    bootstrapRequirement1:
      "The deployment has a bootstrap secret configured in server-side secret storage.",
    bootstrapRequirement2:
      "No administrator exists yet. Afterwards this page stops working.",
    bootstrapRequirement3:
      "The account below is already registered and its email address is verified.",
    bootstrapSecret: "Bootstrap secret",
    bootstrapSecretHint:
      "Held in Secret Manager. It is never stored in this page, the repository or the frontend build.",
    bootstrapButton: "Grant administrator role",
    bootstrapDoneHeading: "Administrator created",
    bootstrapDoneBody:
      "The administrator role was granted to {email}. Sign in again so the new session carries the role.",

    notAdminTitle: "Administrator access required",
    notAdminBody:
      "This area is restricted. Your account does not have administrator access.",
  },

  common: {
    loading: "Loading…",
    loadMore: "Load more",
    retry: "Try again",
    back: "Back",
    close: "Close",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    delete: "Delete",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    none: "None",
    notProvided: "Not provided",
    search: "Search",
    copied: "Copied",
    learnMore: "Learn more",
    requiredFields: "Fields marked required must be completed.",
    unsavedChanges: "You have unsaved changes.",
    offline: "You appear to be offline. Check your connection and try again.",
  },

  errors: {
    title: "Something went wrong",
    generic: "Something went wrong. Please try again.",
    network:
      "We could not reach the server. Check your connection and try again.",
    validation_failed: "Please correct the highlighted fields.",
    unauthenticated: "Please sign in to continue.",
    email_not_verified: "Verify your email address before continuing.",
    forbidden: "You do not have permission to do that.",
    not_found: "We could not find that item.",
    rate_limited: "Too many attempts. Please wait a moment and try again.",
    payload_too_large: "That file is too large.",
    unsupported_media_type: "That file format is not supported.",
    submission_not_editable:
      "This application has been submitted and can no longer be edited.",
    submission_not_submittable:
      "This application cannot be submitted in its current state.",
    submission_not_deletable: "Only drafts can be deleted.",
    consent_to_contact_required: "Please confirm we may contact you.",
    consent_to_privacy_required:
      "Please confirm you have read the privacy notice.",
    video_quota_exceeded: "You have reached the limit of stored recordings.",
    video_not_playable: "This recording is not available for playback.",
    video_already_uploaded: "This recording has already been uploaded.",
    upload_object_missing:
      "The uploaded file could not be found. Please upload it again.",
    upload_empty: "The recording was empty. Please record again.",
    recording_too_long: "The recording is longer than the allowed maximum.",
    bootstrap_already_used: "An administrator already exists.",
    last_admin: "At least one administrator must remain.",
    cannot_demote_self: "You cannot remove your own administrator role.",
    cannot_disable_self: "You cannot disable your own account.",
    adapter_not_configured:
      "This feature is not configured for the current deployment region.",
    internal_error:
      "Something went wrong on our side. Please try again shortly.",
    notFoundTitle: "Page not found",
    notFoundBody: "The page you requested does not exist or has moved.",
    notFoundAction: "Go to the home page",
    // Mapped from Firebase Authentication error codes by the auth adapter.
    auth: {
      invalid_credentials:
        "That email address and password do not match an account.",
      email_in_use: "An account already exists for that email address.",
      weak_password: "Choose a longer password — at least 12 characters.",
      invalid_email: "Enter a valid email address.",
      too_many_requests:
        "Too many attempts. Please wait a few minutes and try again.",
      user_disabled:
        "This account has been disabled. Contact us if you think this is a mistake.",
      requires_recent_login: "Please sign in again to complete this change.",
      network:
        "We could not reach the sign-in service. Check your connection and try again.",
      unknown: "Sign-in failed. Please try again.",
    },
  },

  legal: {
    privacyTitle: "Privacy notice",
    privacyUpdated:
      "This notice describes the current behaviour of this website.",
    privacyCollectHeading: "What we collect",
    privacyCollectBody:
      "If you send a contact message we store the name, email address, interest, organization and message you provide. If you create an account we store your email address, your display name, and any optional organization and phone number you add. If you submit an application we store your answers. If you record a video introduction we store the video file and its size, format and status.",
    privacyAssessmentHeading: "The GrowthWise Check-In",
    privacyAssessmentBody:
      "Answers to the check in are processed entirely in your browser. They are not transmitted to us and not saved anywhere.",
    privacyUseHeading: "How we use it",
    privacyUseBody:
      "We use what you provide to reply to you and to administer the programs you ask about. We do not sell it and we do not use it for advertising.",
    privacySecurityHeading: "Storage and access",
    privacySecurityBody:
      "Data is stored in the United States. Video recordings are private: they have no public link, and playback requires an authenticated, short-lived, individually authorised URL. GrowthWise Education administrators can read applications and play recordings in order to respond to them.",
    privacyRetentionHeading: "How long we keep it",
    privacyRetentionBody:
      "A retention period for applications and recordings has not yet been set. Until it is, nothing is deleted automatically, and you can delete your own drafts and recordings at any time from your account.",
    privacyRightsHeading: "Your choices",
    privacyRightsBody:
      "You can edit your profile, delete your drafts and delete your recordings from your account. To ask about anything else, contact us at the address on the contact page.",
    privacyContactHeading: "Questions",
    privacyContactBody: "Contact us at hello@growthwiseeducation.org.",
    termsTitle: "Terms of use",
    termsEducationalHeading: "Educational purpose",
    termsEducationalBody:
      "GrowthWise Education provides educational programs and materials. Nothing on this website is a medical or psychological diagnosis, and nothing here substitutes for assessment or care from a qualified healthcare or mental-health professional.",
    termsAccountHeading: "Your account",
    termsAccountBody:
      "Keep your password private and tell us if you believe someone else has used your account. You are responsible for the content you submit, including anything you record.",
    termsContentHeading: "What you upload",
    termsContentBody:
      "Only upload recordings of yourself, and only record in a place where you are comfortable being recorded. Do not upload anything unlawful or anything containing other people without their agreement.",
    termsAvailabilityHeading: "Availability",
    termsAvailabilityBody:
      "This is a live service and may change. We may suspend access to protect the service or its users.",
    termsCrisisHeading: "Urgent situations",
    termsCrisisBody:
      "This website is not monitored for emergencies. For urgent or crisis support in the United States, call or text 988. In other countries, contact the appropriate local emergency or crisis-support service.",
  },

  // Shown when a feature has no adapter for the selected deployment region.
  // Never dressed up as a working integration.
  notConfigured: {
    title: "Not configured",
    body: "This feature has no provider configured for the {region} deployment. It is a defined interface awaiting an implementation, not a working integration.",
    region: "Deployment region: {region}",
  },
} as const;

/**
 * The catalogue contract.
 *
 * `Widen` turns the string literals above into plain `string`, so another
 * language must match the *shape* of the English catalogue without being
 * forced to repeat its exact words. Every key and every array length is
 * still enforced by the compiler.
 */
export type Messages = Widen<typeof en>;

type Widen<T> = T extends string
  ? string
  : T extends readonly (infer U)[]
    ? // Arrays stay readonly: the English catalogue is declared `as const`,
      // and a mutable tuple type would reject it.
      { readonly [K in keyof T]: Widen<U> }
    : { -readonly [K in keyof T]: Widen<T[K]> };
